<template>
  <div :class="$vuetify.display.xs ? 'pl-8' : 'px-8'">
    <v-row row wrap>
      <v-col cols="12">
        <MessageDialog
          :show-dialog="showMessageDialog"
          :has-button-two="true"
          type="warning"
          :title="$t('L_cancel_changes_bank_acct')"
          :message="$t('L_by_cancelling_no_change')"
          :button-two-label="$t('L_yes_cancel')"
          :button-one-label="$t('L_no_continue')"
          :is-persistent="true"
          @buttonTwoAction="cancel"
          @buttonOneAction="showMessageDialog = false"
        />
        <div class="card-ach-wrapper">
          <div class="text-align-left pt-4 pb-4">
            <LoadingIndicator :loading="!processCompleted" />
            <h4>{{ $t('L_please_review_your_updated_bank_account_information') }}</h4>
          </div>
          <div class="backgroundlighblue py-2 pl-2 pr-4 mb-4">
            <v-row wrap class="pl-2">
              <v-col xs="6" class="font-size--14 mt-4">
                {{ $t('L_bank_account') }}
              </v-col>
              <v-col cxs="6" class="mt-2 pr-4 text-right">
                <OnexButton @click="goToPreviousPage" :disabled="submitClicked === true">
                  <span class="changeLabel">{{ $t('L_change') }}</span>
                </OnexButton>
              </v-col>
              <v-col cols="12" class="font-size--16 font-weight-bold">
                {{ hideNums(bankInfo.accountNumber) }}
                {{ bankInfo.accountTypeString }}
              </v-col>
              <v-col cols="12" class="mt-2 mb-4 font-size--14 font-weight-regular">
                {{ bankInfo.bankName }}
              </v-col>
            </v-row>
          </div>
          <v-card class="setMaxHeightCard my-8" :rounded="false">
            <v-card-text>
              <v-row wrap mt-4>
                <v-col cols="12" class="font-weight-bold" font-size--16 mb-4>
                  <span v-html="disclaimerTitle" />
                </v-col>
                <v-col cols="12" font-weight-regular font-size--14 mb-8>
                  <span v-html="disclaimerBody" />
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
          <v-row wrap mt-4 pt2>
            <v-col cols="12">
              <v-checkbox
                v-model="accepted"
                :label="$t('L_by_checking_the_box_and_clicking_I_authorize')"
              />
            </v-col>
          </v-row>
          <div class="pb-4 mb-4">
            <v-row class="d-flex justify-end my-4">
              <v-col cols="12" sm="4" md="3" class="px-4">
                <OnexButton
                  block
                  variant="outlined"
                  color="outlined"
                  @click="showMessageDialog = true"
                >
                  {{ $t('L_cancel') }}
                </OnexButton>
              </v-col>
              <v-col cols="12" sm="4" md="3" class="px-4">
                <OnexButton block :disabled="!accepted" @click="updateBank">
                  {{ $t('L_i_authorize') }}
                </OnexButton>
              </v-col>
            </v-row>
          </div>
        </div>
      </v-col>
    </v-row>
  </div>
</template>
<script lang="ts">
import { defineComponent, type ComponentOptions } from 'vue';
import { mapState, mapActions } from 'pinia';
import { filters } from '@/mixins/filters';
import MessageDialog from '../../../../../components/MessageDialog.vue';
import LoadingIndicator from '../../../../../components/LoadingIndicator.vue';
import { useCustomerStore } from '@/stores/customer/customer.store';
import { usePolicyStore } from '@/modules/policy/store/policy.store';
import { useGlobalStore } from '@/stores/global/global.store';
import { OnexButton } from '@primerica/onex-ui-lib';

const componentOptions: ComponentOptions = {
  name: 'BankAccountTypeChangeStepTwo',
  mixins: [filters],
  components: {
    MessageDialog,
    LoadingIndicator,
    OnexButton,
  },
  props: {
    bankInfo: {
      type: Object,
      required: true,
      default: () => ({ accountTypeString: '', bankName: '' }),
    },
    isUpdateBank: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    accepted: false,
    processCompleted: true,
    disclaimer: '',
    submitClicked: false,
    showMessageDialog: false,
  }),
  computed: {
    ...mapState(useGlobalStore, {
      dateFormat: 'getDateFormat',
      locale: 'getLocale',
    }),
    ...mapState(usePolicyStore, [
      'currentPolicy',
      'paymentInfoShowPaymentAmountDateBillingMethod',
      'bankName',
    ]),
    ...mapState(useCustomerStore, ['customerProfile']),
    successMessage() {
      if (this.isUpdateBank) {
        return 'L_update_bank_success';
      }
      return 'L_direct_to_ach_success';
    },
    disclaimerTitle() {
      return this.isCanadaPolicy
        ? this.$t('Bank_Account_L_disclaimer_title_ca')
        : this.$t('Bank_Account_L_disclaimer_title_us');
    },
    disclaimerBody() {
      return this.isCanadaPolicy
        ? this.$t('Bank_Account_L_disclaimer_body_ca')
        : this.$t('Bank_Account_L_disclaimer_body_us');
    },
    isCanadaPolicy() {
      return this.currentPolicy?.isCanadaPolicy;
    },
  },
  methods: {
    ...mapActions(useGlobalStore, {
      addError: 'addError',
      addAlert: 'addAlert',
      removeAlerts: 'removeAlerts',
    }),
    ...mapActions(usePolicyStore, {
      getPaymentInfo: 'getPaymentInfo',
      getPolicies: 'getPolicies',
      updateBillingMode: 'updateBillingMode',
    }),
    hideNums(value: string) {
      if (value.length > 5) {
        return (
          value.substring(0, value.length - 4).replace(/\d/g, '*') +
          value.substring(value.length - 4)
        );
      } else if (value.length === 2) {
        return (
          value.substring(0, value.length - 1).replace(/\d/g, '*') +
          value.substring(value.length - 1)
        );
      }
      return (
        value.substring(0, value.length - 2).replace(/\d/g, '*') + value.substring(value.length - 2)
      );
    },
    async updateBank() {
      if (this.processCompleted === false) {
        return;
      }
      this.processCompleted = false;
      const draftAmount = this.bankInfo.draftAmount || '';
      // this.accepted = true;
      try {
        const paymentInfo = {
          type: 'BANK',
          nextCycleDate: this.bankInfo.nextCycleDate,
          draftAmount: draftAmount.replace(',', ''), // Remove comma
          reqMode: this.bankInfo.reqMode,
          draftDay: this.bankInfo.draftDay,
          emailAddress: this.customerProfile.email,
          language: this.locale,
          bankAccount: {
            accountName: this.bankInfo.accountName,
            routingNumber: this.bankInfo.routingNumber,
            accountNumber: this.bankInfo.accountNumber,
            accountType: this.bankInfo.accountType === 1 ? '3' : ' ',
          },
        };
        this.submitClicked = true;
        const resp = await this.updateBillingMode({
          paymentInfo,
          policyNumber: this.bankInfo.policyNumber,
        });
        if (resp) {
          /*
           * Emit a custom action when a user successfully submits a bank/payment info change
           * https://www7.v1host.com/Primerica/attachment.img/284635/Analytics%20User%20Stories%20DT_3.30.21.docx
           */
          // const m = (this.isUpdateBank ? 'bank_info_changes_saved' : 'payment_info_changes_saved');
          // const i = this.$dynatrace.enterAction(m);
          // this.$dynatrace.leaveAction(i);
          this.showCompleteBanner();
          await this.refreshPolicies();
          // if (resStatus === 200) {
          await this.refreshPaymentData();
          this.$emit('goToStepThree', this.bankInfo);
        } else {
          this.showErrorBanner();
        }
        this.paymentInfoShowPaymentAmountDateBillingMethod = true;
        this.submitClicked = false;
      } catch (_error) {
        /* empty */
      } finally {
        // this.accepted = false;
        this.processCompleted = true;
      }
    },
    refreshPolicies() {
      return this.getPolicies();
    },
    refreshPaymentData() {
      this.getPaymentInfo(this.bankInfo.policyNumber);
    },
    showSuccessBanner() {
      this.addAlert({
        type: 'success',
        details: {
          message: this.$t('L_a_monthly_payment_will_be_deducted', {
            amount: `${this.bankInfo.draftAmount}`,
          }),
        },
        context: 'payment',
        autoDismiss: false,
      });
    },
    showCompleteBanner() {
      this.addAlert({
        type: 'success',
        details: { message: this.$t(this.successMessage, { email: this.customerProfile.email }) },
        context: 'payment',
        autoDismiss: false,
      });
    },

    showErrorBanner() {
      this.addError({
        error: {
          key: 'L_your_request_cannot_be_processed_at_this_time',
          message: this.$t('L_your_request_cannot_be_processed_at_this_time'),
        },
        context: 'payment',
      });
    },
    async goToPreviousPage() {
      await this.$emit('goToStepOne');
      this.accepted = false;
    },
    cancel() {
      this.bankName = null; // clearBank()
      this.$emit('cancel');
    },
    // draftDay(value) {
    //   return value.charAt(0) === '1' ? `${value}th` : this.addExt(value);
    // },
    // addExt(val) {
    //   let ext;
    //   switch (val.charAt(1)) {
    //     case '1':
    //     case '21':
    //       ext = 'st';
    //       break;
    //     case '2':
    //     case '22':
    //       ext = 'nd';
    //       break;
    //     case '3':
    //     case '23':
    //       ext = 'rd';
    //       break;
    //     default:
    //       ext = 'th';
    //   }
    //   const num = val.charAt(0) === '0' ? val.slice(1) : val;
    //   return `${num}${ext}`;
    // },
    // currency(value) {
    //   return `$${value}`;
    // },
  },
  mounted() {
    this.accepted = false;
  },
};
export default defineComponent(componentOptions);
</script>

<style scoped lang="scss">
.setMaxHeightCard {
  display: flex !important;
  flex-direction: column;
  height: 200px;
  flex: 0 1 auto;
  overflow-y: auto;
}
.grey-ada-text {
  color: #737171;
}
.card-ach-wrapper {
  position: relative;
}
#alerts {
  position: -webkit-sticky;
  position: sticky;
  top: 4rem;
  z-index: 1;
}
.backgroundlighblue {
  background: var(--Slate-F5F7FA, #f5f7fa);
}
.centerPage {
  position: absolute;
  top: 50%;
  left: 50%;
}
</style>
